import React, { useEffect } from 'react'
import { twMerge } from 'tailwind-merge'

interface ScrollImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  containerProps?: React.HTMLAttributes<HTMLDivElement>
  scrollDuration?: number
  resetDuration?: number
}

export const ScrollImage = ({
  containerProps,
  scrollDuration = 8,
  resetDuration = 1.5,
  ...imgProps
}: ScrollImageProps) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const imgRef = React.useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    if (!imgRef.current) {
      return
    }

    imgRef.current.style.transform = 'translateY(0)'
  }, [])

  const handlePointerEnter = () => {
    const img = imgRef.current
    const container = containerRef.current

    if (!img || !container) {
      return
    }

    const maxScroll = img.clientHeight - container.clientHeight

    if (maxScroll <= 0) {
      return
    }

    img.style.transition = `transform ${scrollDuration}s linear`
    img.style.transform = `translateY(-${maxScroll}px)`
  }

  const handlePointerLeave = () => {
    const img = imgRef.current

    if (!img) {
      return
    }

    img.style.transition = `transform ${resetDuration}s ease-in-out`
    img.style.transform = 'translateY(0)'
  }

  return (
    <div
      ref={containerRef}
      {...containerProps}
      className={twMerge('overflow-hidden', containerProps?.className)}
    >
      <img
        ref={imgRef}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        {...imgProps}
        alt={imgProps.alt ?? ''}
        className={twMerge(
          'h-auto w-full transition-all duration-300',
          imgProps.className,
        )}
      />
    </div>
  )
}
